import React, { ReactNode, useEffect, useState } from "react"
import {
  EventMessage,
  EventType,
  PublicClientApplication,
} from "@azure/msal-browser"
import { MsalProvider } from "@azure/msal-react"
import { msalConfig } from "./authConfig"
import { resolveMsalAccount } from "./authToken"
import { gatsbyNavigationClient } from "../Utils/NavigationClient"

let msalInstance: PublicClientApplication | undefined

/**
 * The instance is built during the first client render so MsalProvider can be
 * mounted right away. Rendering nothing while MSAL boots leaves Gatsby's server
 * markup unhydrated, and React then keeps that stale tree in the DOM and
 * appends a second, client rendered one below it.
 */
const getMsalInstance = () => {
  if (!msalInstance) {
    const instance = new PublicClientApplication(msalConfig)
    instance.setNavigationClient(gatsbyNavigationClient)

    instance.addEventCallback((event: EventMessage) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS &&
        event.payload &&
        "account" in event.payload &&
        event.payload.account
      ) {
        instance.setActiveAccount(event.payload.account)
      }
    })

    msalInstance = instance
  }

  return msalInstance
}

const ClientMsalProvider = ({ element }: { element: ReactNode }) => {
  const [instance] = useState(getMsalInstance)
  const [initFailed, setInitFailed] = useState(false)

  useEffect(() => {
    // MsalProvider runs initialize and handleRedirectPromise itself and keeps
    // inProgress on "startup" until they settle, so awaiting the same cached
    // promises here only adds the active account. Recovering from a cache that
    // can no longer produce a token is left to ErrorComponent, which sees the
    // failure the auth gate reports instead of racing it.
    const restoreActiveAccount = async () => {
      await instance.initialize()
      await instance.handleRedirectPromise()

      const account = resolveMsalAccount(instance.getAllAccounts())
      if (account) {
        instance.setActiveAccount(account)
      }
    }

    restoreActiveAccount().catch(error => {
      console.error("MSAL initialization failed:", error)
      setInitFailed(true)
    })
  }, [instance])

  if (initFailed) {
    return (
      <div className="bg-gray px-8 py-8 pb-16 lg:px-32 lg:pb-32">
        <h1 className="font-sans text-blue text-xl">
          Det har skjedd en feil ved innlogging
        </h1>
        <p className="font-sans">
          Vi klarte ikke å starte innloggingen. Prøv å laste siden på nytt.
        </p>
      </div>
    )
  }

  return <MsalProvider instance={instance}>{element}</MsalProvider>
}

const Provider = ({ element }: { element: ReactNode }) => {
  if (typeof window === "undefined") {
    return element
  }

  return <ClientMsalProvider element={element} />
}

export default Provider
